import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import WhatsAppHandoffCard from "../../../../widget-v2/src/components/WhatsAppHandoffCard.jsx";
import { useBot } from "../../hooks/useApi";
import { QuandoUsarla, useAttivazione } from "./shared";
import { BarraSalvataggio } from "./casella";
import { SPAZI } from "./brand";
import { Gruppo, Passi } from "../widget/campi";
import ChatPreview from "./ChatPreview";

// Il server conserva solo le cifre: nel campo il numero si rilegge con il "+".
const numeroLeggibile = (phone) => (phone ? `+${phone}` : "");
const soloCifre = (phone) => String(phone || "").replace(/\D/g, "");

// Il pulsante WhatsApp sulle schede prodotto che il bot mostra in chat.
const SCHEDE_PRODOTTO = [
  { value: "learn-more", label: "No, solo «Scopri di più»" },
  { value: "both", label: "Sì, accanto a «Scopri di più»" },
  { value: "ask-whatsapp", label: "Sì, al posto di «Scopri di più»" },
];

// Le regole dell'invito si scrivono come testo: vuoto = regola spenta.
const regola = (valore) => (valore === null || valore === undefined ? "" : String(valore));

const daImpostazioni = (settings) => ({
  phone: numeroLeggibile(settings.phone),
  button_label: settings.button_label || "",
  use_when: settings.use_when || "",
  product_action: settings.product_action || "learn-more",
  popup: {
    enabled: Boolean(settings.popup?.enabled),
    after_seconds: regola(settings.popup?.after_seconds),
    after_messages: regola(settings.popup?.after_messages),
    message: settings.popup?.message || "",
    button_label: settings.popup?.button_label || "",
  },
  quick_reply: {
    enabled: Boolean(settings.quick_reply?.enabled),
    text: settings.quick_reply?.text || "",
  },
});

const ESEMPIO_CHAT = [
  { text: "Posso mandarvi le foto del guasto?" },
  { bot: true, text: "Certo: su WhatsApp è più comodo. Il messaggio è già pronto, ti basta inviarlo." },
];

/**
 * Dettaglio dell'app "Continua su WhatsApp": numero e testo del pulsante,
 * quando proporlo, gli altri punti del widget dove compare. A destra
 * l'anteprima del pulsante in chat. L'interruttore sta nell'intestazione.
 * Il numero e' l'unico del bot: lo usano anche la pillola, le schede
 * prodotto, l'invito a tempo e il chip.
 */
export default function WhatsAppApp({ botId, app, settings, limits, salva, saving, onAttivazione }) {
  const { data: bot } = useBot(botId);
  const salvati = daImpostazioni(settings);
  const firmaSalvati = JSON.stringify(salvati);
  const [bozza, setBozza] = useState(salvati);
  // Dopo un salvataggio i valori tornano dal server (il numero in cifre).
  useEffect(() => {
    setBozza(JSON.parse(firmaSalvati));
  }, [firmaSalvati]);

  const modificato = JSON.stringify(bozza) !== firmaSalvati;
  const patch = (changes) => setBozza((prev) => ({ ...prev, ...changes }));
  const patchPopup = (changes) => setBozza((prev) => ({ ...prev, popup: { ...prev.popup, ...changes } }));
  const patchChip = (changes) => setBozza((prev) => ({ ...prev, quick_reply: { ...prev.quick_reply, ...changes } }));
  const numero = soloCifre(bozza.phone);
  const salvaEAttiva = () => salva({ whatsapp: { ...bozza, enabled: true } }, "WhatsApp attivo sull'assistente");
  // Con il numero scritto l'interruttore in alto accende e salva tutto.
  useAttivazione(onAttivazione, Boolean(numero), salvaEAttiva);

  return (
    <div className={`grid items-start ${SPAZI.campiAnteprima} xl:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]`}>
      <div className="min-w-0 space-y-8">
        {settings.available === false ? (
          <div className="flex items-start gap-2.5 rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-sm text-amber-900">
              Il piano di questa organizzazione non include WhatsApp: puoi prepararlo, ma in chat e nel
              widget non comparirà finché non passi a un piano che lo prevede.{" "}
              <Link to="/billing" className="font-medium underline underline-offset-2">
                Vedi i piani
              </Link>
            </p>
          </div>
        ) : null}

        <Passi>
          <Gruppo
            fatto={Boolean(settings.phone)}
            titolo="Il tuo numero"
            descrizione="Dove arriva il visitatore quando preme il pulsante."
            className="grid gap-4 sm:grid-cols-2"
          >
            <div className="space-y-1.5">
              <Label htmlFor="wa-numero" className="text-xs font-medium text-[#334155]">
                Numero WhatsApp
              </Label>
              <Input
                id="wa-numero"
                inputMode="tel"
                value={bozza.phone}
                placeholder="+39 333 1234567"
                onChange={(e) => patch({ phone: e.target.value })}
              />
              <p className="text-xs text-[#64748B]">
                {settings.phone
                  ? "Con il prefisso internazionale."
                  : "Inserisci il numero e salva: poi potrai attivare l'app."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wa-pulsante" className="text-xs font-medium text-[#334155]">
                Testo del pulsante
              </Label>
              <Input
                id="wa-pulsante"
                maxLength={60}
                value={bozza.button_label}
                placeholder="Continua su WhatsApp"
                onChange={(e) => patch({ button_label: e.target.value })}
              />
            </div>
          </Gruppo>

          <Gruppo
            titolo="Quando proporlo"
            descrizione="Chi scrive “WhatsApp” in chat vede il pulsante in ogni caso."
            facoltativo
          >
            <QuandoUsarla
              id="quando-whatsapp"
              value={bozza.use_when}
              onChange={(use_when) => patch({ use_when })}
              placeholder={settings.default_use_when}
              max={limits?.use_when_max || 600}
              examples={app.examples}
            />
          </Gruppo>

          <Gruppo
            titolo="Anche nel widget"
            descrizione="Altri punti dove il visitatore trova WhatsApp, con lo stesso numero."
            facoltativo
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="wa-schede" className="text-xs font-medium text-[#334155]">
                Pulsante WhatsApp sulle schede prodotto
              </Label>
              <Select value={bozza.product_action} onValueChange={(product_action) => patch({ product_action })}>
                <SelectTrigger id="wa-schede" className="h-9 max-w-sm rounded-[10px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDE_PRODOTTO.map((voce) => (
                    <SelectItem key={voce.value} value={voce.value}>
                      {voce.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#64748B]">
                Vale quando l'assistente mostra i tuoi prodotti in chat: il visitatore chiede di quel prodotto su WhatsApp.
              </p>
            </div>
            <div className="space-y-3 border-t border-[#E2E8F0] pt-4">
              <Opzione
                id="wa-invito"
                titolo="Invito che compare da solo in chat"
                spiegazione="Un riquadro «Continua su WhatsApp» dopo un po' di tempo o qualche messaggio, anche se il visitatore non lo chiede."
                acceso={bozza.popup.enabled}
                onCambia={(enabled) => patchPopup({ enabled })}
              />
              {bozza.popup.enabled && (
                <div className="grid gap-4 rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4 sm:grid-cols-2">
                  <CampoRegola
                    id="wa-invito-secondi"
                    label="Dopo quanti secondi"
                    value={bozza.popup.after_seconds}
                    limiti={limits?.popup?.seconds}
                    onChange={(after_seconds) => patchPopup({ after_seconds })}
                  />
                  <CampoRegola
                    id="wa-invito-messaggi"
                    label="Dopo quanti messaggi del visitatore"
                    value={bozza.popup.after_messages}
                    limiti={limits?.popup?.messages}
                    onChange={(after_messages) => patchPopup({ after_messages })}
                  />
                  <p className="text-xs text-[#64748B] sm:col-span-2">
                    Lascia vuoto per non usare quella regola. Compare al massimo una volta al giorno per visitatore.
                  </p>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="wa-invito-testo" className="text-xs font-medium text-[#334155]">
                      Testo dell'invito
                    </Label>
                    <Input
                      id="wa-invito-testo"
                      maxLength={200}
                      value={bozza.popup.message}
                      placeholder="Vuoi continuare questa conversazione? Siamo disponibili su WhatsApp!"
                      onChange={(e) => patchPopup({ message: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wa-invito-pulsante" className="text-xs font-medium text-[#334155]">
                      Testo del pulsante dell'invito
                    </Label>
                    <Input
                      id="wa-invito-pulsante"
                      maxLength={40}
                      value={bozza.popup.button_label}
                      placeholder="Scrivici su WhatsApp"
                      onChange={(e) => patchPopup({ button_label: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-3 border-t border-[#E2E8F0] pt-4">
              <Opzione
                id="wa-chip"
                titolo="Chip tra le risposte rapide"
                spiegazione="Un pulsante WhatsApp accanto alle risposte suggerite, all'inizio della chat."
                acceso={bozza.quick_reply.enabled}
                onCambia={(enabled) => patchChip({ enabled })}
              />
              {bozza.quick_reply.enabled && (
                <div className="max-w-sm space-y-1.5">
                  <Label htmlFor="wa-chip-testo" className="text-xs font-medium text-[#334155]">
                    Testo del chip
                  </Label>
                  <Input
                    id="wa-chip-testo"
                    maxLength={40}
                    value={bozza.quick_reply.text}
                    placeholder="Continua su WhatsApp"
                    onChange={(e) => patchChip({ text: e.target.value })}
                  />
                </div>
              )}
            </div>
            <p className="border-t border-[#E2E8F0] pt-4 text-xs text-[#64748B]">
              La pillola «Scrivici su WhatsApp» accanto alla bolla si accende dal{" "}
              <Link to={`/bots/${botId}/widget`} className="font-medium text-[#18181B] underline underline-offset-2">
                Widget
              </Link>
              , in Interazione rapida.
            </p>
          </Gruppo>
        </Passi>

        <BarraSalvataggio
          modificato={modificato}
          saving={saving}
          attiva={settings.enabled}
          puoAttivare={Boolean(numero)}
          onSalva={() => salva({ whatsapp: bozza }, "Impostazioni di WhatsApp salvate")}
          onSalvaEAttiva={salvaEAttiva}
        />
      </div>

      <ChatPreview
        theme={bot?.widget_config?.theme}
        titolo={bot?.name}
        messaggi={ESEMPIO_CHAT}
        piede={
          numero ? (
            <p>Il messaggio lo scrive l'assistente a partire dalle domande del visitatore, che può cambiarlo prima di inviarlo.</p>
          ) : (
            <p>Senza numero il pulsante non compare: qui vedi come sarà.</p>
          )
        }
      >
        <WhatsAppHandoffCard
          card={{
            url: `https://wa.me/${numero || "390000000000"}`,
            prefill: "Ciao, vi scrivo dalla chat del sito. Posso mandarvi le foto del guasto?",
            button_label: bozza.button_label.trim() || "Continua su WhatsApp",
          }}
        />
      </ChatPreview>
    </div>
  );
}

/** Un'opzione con interruttore: titolo, spiegazione e switch a destra. */
function Opzione({ id, titolo, spiegazione, acceso, onCambia }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium text-[#18181B]">
          {titolo}
        </Label>
        <p className="text-xs text-[#64748B]">{spiegazione}</p>
      </div>
      <Switch id={id} checked={acceso} onCheckedChange={onCambia} />
    </div>
  );
}

/** Una regola dell'invito: un numero, o vuoto per non usarla. */
function CampoRegola({ id, label, value, limiti, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-[#334155]">
        {label}
      </Label>
      <Input
        id={id}
        inputMode="numeric"
        value={value}
        placeholder="—"
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      />
      {limiti && (
        <p className="text-xs text-[#64748B]">
          Da {limiti.min} a {limiti.max}.
        </p>
      )}
    </div>
  );
}
